import {h} from 'preact';
import {Redirect, Link} from 'react-router-dom';
import {
    EquipmentList,
    EquipmentDetail,
    RmaDetail,
    RepairsCart,
    RepairsCheckout,
    RepairsConfirmation
} from './pages';
import {RmaList, RmaListContainer} from './Rma';
import {PageHeading, SmsPageError} from './components';
import {getEquipmentById, getRmaByRequestId, groupLineItemsByAvailability} from './helpers';
import {box} from '@containers/SmsPortal/styles';

export const routes = [
    {
        path: '/',
        exact: true,
        render: routeContext => props => <Redirect {...props} to='/my-equipment'/>
    },
    {
        path: '/my-equipment',
        exact: true,
        pageHeading: () => <PageHeading title="My Equipment"
                                        subTitle="From your equipment purchases, choose the item you would like repaired."/>,
        render: routeContext => props => {
            if ( routeContext.equipmentData.error ) {
                return <SmsPageError/>
            }

            return <EquipmentList{...props}
                                 equipmentList={routeContext.equipmentList}
                                 addressList={routeContext.customerAddresses}/>
        }
    },
    {
        path: '/my-equipment/:serialId',
        exact: true,
        pageHeading: () => <PageHeading title="Request Repair"/>,
        render: routeContext => props => {
            const {serialId} = props.match.params;
            const item = getEquipmentById( decodeURIComponent( serialId ), routeContext.equipmentList )[0];
            return (item)
                ?
                <EquipmentDetail {...props} actions={routeContext.actions} equipmentItem={item}/>
                :
                <div className="text-center mt-8">
                    <h2 className="text-lg font-normal px-8">Equipment with serial number ({serialId}) not found</h2>
                    <p className="mt-1">Return to <Link to={`/my-equipment`}>My Equipment</Link></p>
                </div>
        }
    },
    {
        path: '/cart',
        exact: true,
        pageHeading: () => <PageHeading title="Repairs Cart"/>,
        render: routeContext => props => {
            const lines = groupLineItemsByAvailability( routeContext.cartLineData );

            return <RepairsCart{...props}
                               actions={routeContext.actions}
                               list={lines.included || []}
                               exclusions={lines.excluded || []}/>
        }
    },
    {
        path: '/checkout',
        exact: true,
        pageHeading: () => <PageHeading
            title="Finalize your request for repairs"
            subTitle="You are submitting a request for repair. Once we receive your equipment a qualified
                    Ultradent technician will inspect your equipment and determine what repairs are needed. You
                    will receive a detailed quote of the repairs to be performed before any work done
                    where you will have the opportunity to accept or decline the repairs."
        />,
        render: routeContext => props => {
            const {location} = props;
            const lines = groupLineItemsByAvailability( routeContext.cartLineData );
            const comingFromCart = location.state && location.state.fromPath === '/cart';

            // if there are excluded items and the previous route was NOT "/cart"
            // then redirect to cart to notify customer of current exclusions
            if ( lines.excluded && lines.excluded.length && !comingFromCart ) {
                return <Redirect to="/cart"/>;
            }

            if ( !lines.included || !lines.included.length ) {
                routeContext.actions.clearCart(); // note -> clears cart exclusions as well
                return <Redirect to="/cart"/>;
            }

            return <RepairsCheckout {...props} list={lines.included || []} actions={routeContext.actions}/>
        }
    },
    {
        path: '/checkout/confirmation',
        exact: true,
        pageHeading: () => <PageHeading
            title="Almost Done!"
            subTitle="Your request has been authorized, we just need your equipment shipped to us."
        />,
        render: routeContext => props => {
            const {location} = props;
            if ( location.state && location.state.rmas ) {
                return <RepairsConfirmation {...props} rmas={location.state.rmas} actions={routeContext.actions}/>
            }
            return <Redirect to="/my-equipment"/>
        }
    },
    {
        path: '/open-repairs',
        exact: true,
        pageHeading: () => <PageHeading title="Open Repairs"/>,
        render: routeContext => props =>
            <RmaListContainer {...props} rmaData={routeContext.rmaData}>
                {( {rmasOpen, ...containerProps} ) => {
                    if ( rmasOpen.length <= 0 ) {
                        return (
                            <div className="text-center mt-8 max-w-sm mx-auto">
                                <h2 className="text-lg font-normal px-8">No open repairs</h2>
                                <p className="mt-1">If you would like to open a new repair request go to <Link
                                    to={`/my-equipment`}>My Equipment</Link> and select the item from your equipment
                                    list that you need repaired.
                                </p>
                                <Link to={`/my-equipment`} className="button secondary mt-8">View your equipment</Link>
                                <p className={`mt-16 ${box.info}`}>
                                    New repair requests may not show here immediately and may take up to an hour while we process
                                    the request.
                                </p>
                            </div>
                        );
                    }

                    return <RmaList rmas={rmasOpen} {...containerProps} />;
                }}
            </RmaListContainer>
    },
    {
        path: '/repair-history',
        exact: true,
        pageHeading: () => <PageHeading title="Repair History"/>,
        render: routeContext => props =>
            <RmaListContainer {...props} rmaData={routeContext.rmaData}>
                {( {rmasCompleted, ...containerProps} ) => {
                    if ( rmasCompleted.length <= 0 ) {
                        return (
                            <div className="text-center mt-8 max-w-sm mx-auto">
                                <h2 className="text-lg font-normal px-8">No completed repairs</h2>
                                <p className="mt-1">If you would like to open a new repair request go to <Link
                                    to={`/my-equipment`}>My Equipment</Link> and select the item from your equipment
                                    list that you need repaired.
                                </p>
                                <Link to={`/open-repairs`} className="button secondary mt-8">View open repairs</Link>
                            </div>
                        );
                    }

                    return <RmaList rmas={rmasCompleted} {...containerProps} />;
                }}
            </RmaListContainer>
    },
    {
        path: '/open-repairs/:requestId',
        exact: true,
        pageHeading: () => <PageHeading title="Repair Status"/>,
        render: routeContext => props => {
            const item = getRmaByRequestId( props.match.params.requestId, routeContext.customerRmas );
            return item
                ? <RmaDetail {...props} {...item} />
                : <div className="text-center mt-8">
                    <h2 className="text-lg font-normal px-8">A repair with RMA ({props.match.params.requestId}) not
                        found</h2>
                    <p className="mt-1">Return to <Link to={`/open-repairs`}>Open Repairs</Link></p>
                </div>;
        }
    },
    {
        path: '/repair-history/:requestId',
        exact: true,
        pageHeading: () => <PageHeading title="Repair Details"/>,
        render: routeContext => props => {
            const item = getRmaByRequestId( props.match.params.requestId, routeContext.customerRmas );
            return item
                ? <RmaDetail {...props} {...item} />
                : <div className="text-center mt-8">
                    <h2 className="text-lg font-normal px-8">A repair with RMA ({props.match.params.requestId}) not
                        found</h2>
                    <p className="mt-1">Return to <Link to={`/repair-history`}>Repair History</Link></p>
                </div>;
        }
    }
];